export function getOutOfStock(config) {
  return Array.isArray(config?.outOfStock) ? config.outOfStock : [];
}

export function isOutOfStock(config, topping) {
  return getOutOfStock(config).includes(topping);
}

// Out-of-stock toppings present on an order (flat, including joined parts).
export function containsOutOfStock(order, config) {
  if (!order) {
    return [];
  }
  const outOfStock = getOutOfStock(config);
  if (outOfStock.length === 0) {
    return [];
  }
  const toppings = new Set([
    ...(order.toppings || []),
    ...(order.parts || []).flatMap((part) => part.toppings || []),
  ]);
  return outOfStock.filter((topping) => toppings.has(topping));
}

export function filterOffersForStock(offers, config) {
  if (!Array.isArray(offers)) {
    return [];
  }
  const outOfStock = getOutOfStock(config);
  if (outOfStock.length === 0) {
    return [...offers];
  }
  return offers.filter((offer) => !(offer.toppings || []).some((topping) => outOfStock.includes(topping)));
}

export function getCategoryOf(topping, config) {
  for (const [category, items] of Object.entries(config?.categories || {})) {
    if (Array.isArray(items) && items.includes(topping)) {
      return category;
    }
  }
  return null;
}

// Up to 2 available toppings from the same category as `topping`, picked at
// random (excluding the topping itself, everything out of stock and toppings
// already on the order/offer passed via `exclude`).
export function suggestAlternatives(topping, config, exclude = []) {
  const category = getCategoryOf(topping, config);
  if (!category) {
    return [];
  }
  const items = Array.isArray(config.categories[category]) ? config.categories[category] : [];
  const outOfStock = getOutOfStock(config);
  const candidates = items.filter(
    (item) => item !== topping && !outOfStock.includes(item) && !exclude.includes(item),
  );
  const picks = [];
  while (picks.length < 2 && candidates.length > 0) {
    picks.push(candidates.splice(Math.floor(Math.random() * candidates.length), 1)[0]);
  }
  return picks;
}

// Returns a memoizing wrapper around `suggestAlternatives`: every topping is
// rolled exactly once (first call), later calls return the frozen result, so
// the chips shown to a customer stay static during a page visit. One cache
// instance per component mount (useRef).
export function makeStableSuggestionCache() {
  const cache = new Map();
  return (topping, config, exclude = []) => {
    if (!cache.has(topping)) {
      cache.set(topping, suggestAlternatives(topping, config, exclude));
    }
    return cache.get(topping);
  };
}
